import Head from 'next/head'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { StrapiHead } from 'types/strapi/head'
import { flattenImage, isLocalProvider } from 'utils/strapi/image'
import type { ReactNode } from 'react'

interface PageHeadProps {
  head?: StrapiHead
  image?: string
  children?: ReactNode
  onlyNameInTitle?: boolean
}

export function PageHead({
  head = undefined,
  image = undefined,
  children = null,
  onlyNameInTitle = false,
}: PageHeadProps) {
  const router = useRouter()
  const slug = router.asPath.split('?')[0]

  const { t } = useTranslation('metadata')

  const name = t('name')
  const title = onlyNameInTitle
    ? name
    : `${head?.title || t('title')} | ${name}`
  const description = head?.description || t('description')

  const withLocale = router.locale !== router.defaultLocale

  const url = `https://dift.com${withLocale ? `/${router.locale}` : ''}${
    slug === '/' ? '' : slug
  }`
  const canonical = (head?.canonical || url).replace(/#.*$/, '')

  let headStrapiImage
  if (head?.image) {
    headStrapiImage =
      'data' in head.image ? flattenImage(head.image ?? null) : head.image
  }

  let ogImage = image ?? 'https://dift.com/og-image.jpg'
  if (headStrapiImage) {
    ogImage = isLocalProvider(headStrapiImage)
      ? `${process.env.NEXT_PUBLIC_STRAPI_MEDIA_API_URL}${headStrapiImage.url}`
      : headStrapiImage.url
  }

  const alternates = head?.alternates || []

  return (
    <Head>
      <meta name="author" content="Made with love by Dift" key="author" />

      <title>{title}</title>
      <meta name="description" content={description} key="description" />

      {process.env.NEXT_PUBLIC_APP_ENV !== 'production' ? (
        <meta name="robots" content="noindex,nofollow" key="robots" />
      ) : null}

      <meta property="og:type" content="website" key="og:type" />
      <meta property="og:site_name" content={name} key="og:site_name" />
      <meta property="og:title" content={title} key="og:title" />
      <meta property="og:url" content={url} key="og:url" />
      <meta
        property="og:description"
        content={description}
        key="og:description"
      />
      <meta property="og:image" content={ogImage} key="og:image" />

      <meta property="twitter:card" content="summary" key="twitter:card" />
      <meta
        property="twitter:creator"
        content="@CaptainCause"
        key="twitter:creator"
      />
      <meta property="twitter:title" content={title} key="twitter:title" />
      <meta
        property="twitter:description"
        content={description}
        key="twitter:description"
      />
      <meta property="twitter:image" content={ogImage} key="twitter:image" />

      <link href="https://dift.com/favicon.ico" rel="icon" key="favicon" />
      <link href="https://dift.com/apple-icon.png" rel="apple-touch-icon" />
      <link rel="canonical" href={canonical} key="canonical" />
      {alternates.map(alternate => (
        <link
          key={`${alternate.locale}-${alternate.href}`}
          rel="alternate"
          hrefLang={alternate.locale}
          href={alternate.href}
        />
      ))}
      {children}
    </Head>
  )
}

export function DifterPageHead({
  noIndex = false,
  children = null,
}: {
  noIndex?: boolean
  children?: ReactNode
}) {
  const { t } = useTranslation('metadata')
  const ogImage = 'https://dift.com/give-og-image.jpg'

  return (
    <PageHead
      onlyNameInTitle
      image={ogImage}
      head={{
        description: t('difter.description'),
      }}
    >
      {noIndex && (
        <meta name="robots" content="noindex,nofollow" key="robots" />
      )}
      {children}
    </PageHead>
  )
}
